import { Controller } from "@hotwired/stimulus"
import { GridStack } from "gridstack"

// Wraps GridStack over a dashboard's own .grid-stack container (see
// organizations/dashboards/show.html.erb): the one drag/resize dependency in this app. Each
// .grid-stack-item's gs-x/gs-y/gs-w/gs-h attributes seed GridStack from DashboardWidget's own
// persisted grid_x/y/w/h columns; every drag or resize fires GridStack's own "change" event with
// the widgets that actually moved, which gets bulk-saved back to DashboardsController#reposition
// in one request rather than one per widget.
//
// editableValue is false for a viewer (see DashboardPolicy#update?): GridStack's own staticGrid
// option renders the identical grid with every drag/resize handle disabled, so a viewer sees
// exactly the same layout an admin does, just not able to change it.
export default class extends Controller {
  static values = { editable: Boolean, repositionUrl: String }

  connect() {
    this.grid = GridStack.init({
      staticGrid: !this.editableValue,
      column: 12,
      cellHeight: "80px"
      // float defaults to false: every widget auto-compacts upward to close whatever gap a drag
      // or a removed widget would otherwise leave, so the vertical space between rows stays the
      // margin GridStack itself puts there, not whatever arbitrary gap a widget happened to get
      // dropped at. This app shipped with float: true from the start (no widget count small
      // enough to notice); once a dashboard grew a handful of rows, that gap became genuinely
      // inconsistent from row to row, confirmed live by disabling float on an 11-widget dashboard
      // that had picked up gaps this way and watching every widget snap into a tight, even grid.
    }, this.element.querySelector(".grid-stack"))

    if (this.editableValue) {
      this.grid.on("change", () => this.savePositions())
      // See beginInstantScroll's own comment: GridStack's own auto-scroll-while-dragging-near-
      // the-edge logic breaks under this app's global smooth-scroll CSS, so it's suspended for
      // exactly the span of a drag or resize.
      this.grid.on("dragstart resizestart", () => this.beginInstantScroll())
      this.grid.on("dragstop resizestop", () => this.endInstantScroll())
    }
  }

  disconnect() {
    this.endInstantScroll()
    this.grid?.destroy(false)
  }

  // GridStack auto-scrolls the page while a drag/resize nears the viewport's top or bottom edge,
  // by writing the scrolling element's own scrollTop directly and, each animation frame, checking
  // whether that write actually changed anything to decide whether to keep going (see
  // dd-draggable.js's own _autoScrollTick). This app's global html { scroll-behavior: smooth }
  // (see application.bootstrap.scss, for anchor-link jumps) makes that same scrollTop write
  // animate instead of applying immediately, so GridStack's very next synchronous read of
  // scrollTop still sees the old value, reads that as "nothing happened", and cancels its own
  // scroll loop after a single frame -- confirmed live: holding a drag right at the viewport edge
  // scrolled the page only once, by a few pixels, and then sat completely still until the mouse
  // moved again, however slightly, for one more small nudge each time, instead of gliding
  // continuously the way it does with smooth-scroll off (verified the same drag scrolls smoothly
  // and continuously once scroll-behavior is set back to auto for the span of the interaction).
  beginInstantScroll() {
    document.documentElement.classList.add("grid-instant-scroll")
  }

  endInstantScroll() {
    document.documentElement.classList.remove("grid-instant-scroll")
  }

  savePositions() {
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.persist(), 400)
  }

  async persist() {
    const widgets = [ ...this.element.querySelectorAll(".grid-stack-item") ].map((item) => ({
      id: item.dataset.widgetId,
      grid_x: item.getAttribute("gs-x"),
      grid_y: item.getAttribute("gs-y"),
      grid_w: item.getAttribute("gs-w"),
      grid_h: item.getAttribute("gs-h")
    }))

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(this.repositionUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ widgets })
      })
    } catch {
      // A dropped save leaves the layout as GridStack already has it on screen; the next drag
      // or resize (or a plain reload) tries again; not worth interrupting someone mid-arrangement
      // over a network blip.
    }
  }
}
